import { layerStyles, legacyCardLayerStyles } from '~/theme/layer-styles'
import { useIsSanaRebrandEnabled } from '~/theme/useIsSanaRebrandEnabled'
import { merge } from 'lodash'
import React, { isValidElement, ReactElement, RefObject, useRef, useState } from 'react'
import useGroupChildren, { childrenToString } from '~/common/childrenUtils'
import { cq, QueryLabelToStyleValue } from '~/common/containerQuery'
import { Msg } from '~/common/localizationUtils'
import { cloneSafely } from '~/common/reactUtils'
import { $thm } from '~/common/themeSx'
import { AttributeColumn, AttributeGrid, AttributeText } from '~/components/v2/Attribute'
import { BackgroundPattern } from '~/components/v2/BackgroundPattern'
import { ItemsList, ItemsListItem } from '~/components/v2/ItemsList'
import { useActionBarContext } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { useVisibleFields } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.VisibleFieldsButton'
import { BulkActionBar } from '~/components/v2/ResultsLayout/BulkActionBar/BulkActionBar'
import {
  TitleLockupAdditionalInfo,
  TitleLockupBottom,
  TitleLockupFavoriteCheckbox,
  TitleLockupHeading,
  TitleLockupIcon,
  TitleLockupMain,
  TitleLockupSize,
  TitleLockupStatusBadge,
  TitleLockupSubscript,
  TitleLockupSupportText
} from '~/components/v2/TitleLockup'
import {
  Avatar,
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  ButtonGroupActions,
  ButtonGroupProps,
  ButtonProps,
  Checkbox,
  Circle,
  Flex,
  forwardRef,
  Grid,
  GridItem,
  IconButton,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuList,
  Portal,
  SectionHeader,
  StackProps,
  insetDivider
} from '~/design_system'
import { SectionHeaderProps } from '~/design_system/foundations/data_display/Separators'

import { BadgeVariant } from '~/design_system/types'
import { useHoverWithExcludedChildren } from '~/hooks/useHoverWithExcludedChildren'
import { ChevronDown, MoreV } from '~/icons'

type ResultItemsProps = StackProps & {
  cardTemplateColumns?: StackProps['gridTemplateColumns']
}

export const ResultItems = ({ cardTemplateColumns, sx, children, ...props }: ResultItemsProps) => {
  const actionBar = useActionBarContext()
  const isRebrand = useIsSanaRebrandEnabled()
  const DEFAULT_CARD_COLUMNS = 'repeat(auto-fill, minmax(375px, 1fr))'

  const gridCardRadius = 'var(--chakra-radii-xxl)'

  const sharedGridLayout = {
    background: 'none',
    border: 'none',
    boxShadow: 'none',
    // List shell zeros these for square rows. Grid owns xxl on each card.
    '--vndly-legacy-items-list-edge-radius': 'unset',
    '--vndly-items-list-start-radius': 'unset',
    '--vndly-items-list-end-radius': 'unset',
    '[data-items-list-items]': {
      // Do not hide overflow, it cuts off the animated drop shadow on card hover
      overflow: 'visible',
      background: 'none',
      backgroundColor: 'transparent',
      display: 'grid',
      justifyContent: 'center',
      gap: '24px',
      gridTemplateColumns: { base: '1fr', sm: cardTemplateColumns ?? DEFAULT_CARD_COLUMNS }
    }
  }

  const legacyGridStyles = {
    ...sharedGridLayout,
    '[data-items-list-header]': {
      ...legacyCardLayerStyles.card,
      mb: 4,
      mt: -2,
      boxShadow: 'none',
      bg: 'gray.50'
    },
    '[data-results-layout-main]:has(&)': {
      '[data-results-layout-main-content]': {
        overflow: 'visible',
        border: 'none',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        marginTop: 6,
        '[data-nprogress-bar-container]': {
          top: '-25px',
          left: '6px',
          right: '6px'
        }
      }
    },
    '[data-results-layout-main][data--no-spacing]:has(&)': {
      '[data-action-bar]:has(+ :not([data-bulk-action-bar]))': {
        roundedBottom: 'base',
        overflow: 'hidden'
      },
      '[data-action-bar] + [data-bulk-action-bar]': {
        roundedBottom: 'base',
        overflow: 'hidden'
      }
    },
    '[data-items-list-item]': {
      ...legacyCardLayerStyles.card,
      '--vndly-legacy-items-list-edge-radius': gridCardRadius,
      '--vndly-items-list-start-radius': gridCardRadius,
      '--vndly-items-list-end-radius': gridCardRadius,
      flexDirection: 'column',
      borderWidth: '1px',
      borderColor: 'gray.300',
      alignItems: 'flex-start',
      transitionDuration: '.35s',
      transitionTimingFunction: 'ease-in-out',
      transitionProperty: 'box-shadow, transform',
      transform: 'scale3d(1,1,1)',
      willChange: 'transform, box-shadow',
      '&:first-of-type': {
        borderTopWidth: '1px'
      },
      '&:last-of-type': {
        borderTopWidth: '1px'
      },
      '&:hover': {
        transform: 'scale3d(1.01, 1.01, 1.01)',
        boxShadow: '0px 15px 16px 4px rgb(0 0 0 / 10%), -1px 9px 3px 3px rgb(0 0 0 / 3%)'
      },
      '[data-result-gradient-header]': {
        display: 'block'
      },
      '[data-avatar-header]': {
        display: 'block',
        py: 2,
        '[data-gradient-header]': {
          display: 'block'
        }
      },
      '[data-result-content-wrap]': {
        pt: 2
      },
      '[data-checkbox]': {
        mr: 2
      },
      '[data-attribute-grid]': {
        flexDirection: 'column',
        justifyContent: 'flex-start'
      },
      '[data-attribute-column]': {
        margin: 0,
        ml: 0,
        flexGrow: 0,
        flexBasis: 0
      },
      '[data-items-list-button], [data-items-list-link]': {
        alignItems: 'flex-start',
        '&:hover': {
          bg: 'transparent'
        }
      },
      '[data-result-bottom]': {
        display: 'flex',
        gap: '10px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flexWrap: 'wrap'
      }
    }
  }

  const rebrandGridStyles = {
    ...sharedGridLayout,
    '[data-items-list-header]': {
      ...layerStyles.cardTonal,
      mb: 4,
      mt: -2
    },
    '[data-results-layout-main]:has(&)': {
      '[data-results-layout-main-content]': {
        overflow: 'visible',
        border: 'none',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        marginTop: 0,
        '[data-nprogress-bar-container]': {
          top: '-25px',
          left: '6px',
          right: '6px'
        }
      }
    },
    '[data-items-list-item]': {
      ...layerStyles.card,
      '--vndly-legacy-items-list-edge-radius': gridCardRadius,
      '--vndly-items-list-start-radius': gridCardRadius,
      '--vndly-items-list-end-radius': gridCardRadius,
      // Clip the absolute gradient strip to the card radius. Safe here: grid
      // menus Portal out, and box-shadow paints outside the border-box.
      overflow: 'hidden',
      flexDirection: 'column',
      alignItems: 'flex-start',
      '&:first-of-type': {
        borderTopWidth: '1px'
      },
      '&:last-of-type': {
        borderTopWidth: '1px'
      },
      '[data-result-gradient-header]': {
        display: 'block'
      },
      '[data-avatar-header]': {
        display: 'block',
        py: 2,
        '[data-gradient-header]': {
          display: 'block'
        }
      },
      '[data-result-content-wrap]': {
        pt: 2
      },
      '[data-checkbox]': {
        mr: 2
      },
      '[data-attribute-grid]': {
        flexDirection: 'column',
        justifyContent: 'flex-start'
      },
      '[data-attribute-column]': {
        margin: 0,
        ml: 0,
        flexGrow: 0,
        flexBasis: 0
      },
      '[data-items-list-button], [data-items-list-link]': {
        alignItems: 'flex-start',
        '&:hover': {
          bg: 'transparent'
        }
      },
      '[data-result-bottom]': {
        display: 'flex',
        gap: '10px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flexWrap: 'wrap'
      }
    }
  }

  const gridStyles = isRebrand ? rebrandGridStyles : legacyGridStyles

  return (
    <ItemsList
      data-result-items
      data-items-list-display-mode={actionBar.displayMode?.value}
      sx={{
        ...(actionBar.isGridDisplayMode && gridStyles),
        ...sx
      }}
      {...props}>
      {children}
    </ItemsList>
  )
}

ResultItems.displayName = 'ResultItems'

export const LINKS_AND_BUTTONS_EXCLUDING_THE_TITLE =
  'a[href]:not([data-title-lockup-heading-link]), button, input, .chakra-checkbox, [data-hoist-above-parent-hover]'

// The row reads as hovered only while the pointer isn't over one of its own links or buttons.
const ROW_HOVERED = `&:hover:not(:has(:is(${LINKS_AND_BUTTONS_EXCLUDING_THE_TITLE}):hover))`

type AutoResultItemProps = BoxProps & {
  href?: string
  titleLockupSize?: TitleLockupSize
  titleLockupColWidth?: number | string
  attributeColWidths?: QueryLabelToStyleValue[]
  metaColWidth?: QueryLabelToStyleValue
  isCenteredVertically?: boolean
  sx?: BoxProps['sx']
}
/**
 * An easier way to use ResultItem that does not require as much manual composing of various boilerplate
 * containers and particular arrangement of subcomponents.
 *
 * @see ./ResultItem.stories.tsx
 *
 * @description
 * The AutoResultItem just looks for the following children and will automatically place them in the perfect spot:
 * - BulkActionBar.Checkbox (or element with data-slot="bulkCheckbox"). Works automatically with BulkActionBarProvider and BulkActionBar.
 * - Checkbox (or element with data-slot="checkbox"). Have to manually hook up to a BulkActionBarProvider.
 * - TitleLockupHeading (required) (or element with data-slot="title")
 * - TitleLockupSubscript (or element with data-slot="subscript")
 * - TitleLockupFavoriteCheckbox (or element with data-slot="favorite")
 * - TitleLockupStatusBadge (or element with data-slot="statusBadge")
 * - TitleLockupSupportText (or element with data-slot="statusSupport")
 * - AttributeColumn (or element with data-slot="attributeColumn"). You can pass up to 3 AttributeColumns.
 * - ResultAttributes (or element with data-slot="resultAttributes")
 * - ResultMeta (or element with data-slot="resultMeta").
 * - ResultButton (or element with data-slot="resultButton"). You can pass any amount of ResultButtons. We recommend a
 *   max of 2 buttons, but it's up to UX discretion.
 * - ResultActionMenu (or element with data-slot="resultActionMenu"). You can pass up to 1 ResultActionMenu.
 * - ResultOverflowMenu (or element with data-slot="resultOverflowMenu"). You can pass up to 1 ResultOverflowMenu.
 *
 * A list item has various columns whose widths can be adjusted. The defaults are pretty decent, but you'll
 * need to adjust column widths if you:
 * - Have really short titles (and you know they will always be short)
 * - Have some attributes that need a lot more or less space
 * - Have a known-length meta column, and would like to maximize other column widths
 *
 * Here's a little visualization of the "columns" we are talking about:
 * ______________________________________________________________________
 * TitleLockup | Attribute 1 | Attribute 2 | Attribute 3 | Meta/Button(s)
 * ----------------------------------------------------------------------
 *
 * Remember, there can be 0-3 Attribute columns, and 0–1 Meta columns.
 *
 * If you have ResultButtons / a ResultActionMenu / ResultOverflowMenu, those will render into the Meta column.
 *
 * To adjust the column widths, the easy way is to set:
 *
 * - titleLockupColWidth: Sets the titles width.
 *                        Pass a single number | string
 *
 *                        e.g.
 *                        titleLockupColWidth="19%" or titleLockupColWidth="250px"
 *
 * - attributeColWidths: Sets each attribute column individually.
 *                       Pass an array of `ValuesAtComponentBreakpoints<ResultItemsBreakpoints>` objects.
 *                       Typically, you'll set `$base: 'full'`, pass your custom widths to higher breakpoints.
 *
 *                       e.g.
 *                       attributeColWidths={[
 *                         { '@base': 'full', '@xl': '45%' },
 *                         { '@base': 'full', '@xl': '25%' },
 *                         { '@base': 'full', '@xl': '55%' }
 *                       ]}
 *
 * - metaColWidth: Sets the meta column width.
 *                 Pass a `ValuesAtComponentBreakpoints<ResultItemsBreakpoints>` object.
 *
 *                 e.g.
 *                 metaColWidth={ { '@base': 'full', '@xl': '160px' }} // or use %, up to you!
 *
 * @agent-preferred
 * @agent-doc vndly/assets/js/components/v2/ResultsLayout/docs/ContentArea.mdx
 * @agent-gotcha default ContentArea cell for ListLayout. Compose with TitleLockupHeading/TitleLockupStatusBadge/TitleLockupSupportText.
 * @agent-gotcha Don't reach for Table inside ListLayout — there's no official table+ListLayout pattern yet (see gap).
 */
export const AutoResultItem = forwardRef<AutoResultItemProps, 'div'>(
  (
    {
      children,
      sx,
      href,
      titleLockupSize,
      titleLockupColWidth,
      attributeColWidths = [],
      metaColWidth,
      isCenteredVertically = false,
      ...props
    },
    ref
  ) => {
    const extracted = useGroupChildren(
      children,
      {
        gradientHeader: {
          isComponent: ResultGradientHeader,
          isSlot: 'gradientHeader',
          errorName: '<ResultGradientHeader />'
        },
        avatarGradientHeader: {
          isComponent: AvatarGradientHeader,
          isSlot: 'avatarGradientHeader',
          errorName: '<AvatarGradientHeader />'
        },
        checkbox: {
          isComponent: Checkbox,
          isSlot: 'checkbox',
          errorName: '<Checkbox />'
        },
        bulkCheckbox: {
          isComponent: BulkActionBar.Checkbox,
          isSlot: 'bulkCheckbox',
          errorName: '<BulkActionBar.Checkbox />'
        },
        title: {
          min: 1,
          isComponent: TitleLockupHeading,
          isSlot: 'title',
          errorName: '<TitleLockupHeading />'
        },
        subscript: {
          isComponent: TitleLockupSubscript,
          isSlot: 'subscript',
          errorName: '<TitleLockupSubscript />'
        },
        favorite: {
          isComponent: TitleLockupFavoriteCheckbox,
          isSlot: 'favorite',
          errorName: '<TitleLockupFavoriteCheckbox />'
        },
        statusBadge: {
          isComponent: TitleLockupStatusBadge,
          isSlot: 'statusBadge',
          errorName: '<TitleLockupStatusBadge />'
        },
        titleLockupIcon: {
          isComponent: TitleLockupIcon,
          isSlot: 'titleLockupIcon',
          errorName: '<TitleLockupIcon />'
        },
        statusSupport: {
          isComponent: TitleLockupSupportText,
          isSlot: 'statusSupport',
          errorName: '<TitleLockupSupportText />'
        },
        additionalInfo: {
          isComponent: TitleLockupAdditionalInfo,
          isSlot: 'additionalInfo',
          errorName: '<TitleLockupAdditionalInfo />'
        },
        visibleFieldAttributes: {
          isComponent: VisibleFieldAttributes,
          isSlot: 'visibleFieldAttributes',
          errorName: '<VisibleFieldAttributes />'
        },
        attributeColumns: {
          max: 3,
          isComponent: AttributeColumn,
          isSlot: 'attributeColumn',
          errorName: '<AttributeColumn />'
        },
        resultAttributes: {
          isComponent: ResultAttributes,
          isSlot: 'resultAttributes',
          errorName: '<ResultAttributes />'
        },
        meta: {
          isComponent: ResultMeta,
          isSlot: 'resultMeta',
          errorName: '<ResultMeta />'
        },
        buttonGroupActions: {
          isComponent: ButtonGroupActions,
          isSlot: 'buttonGroupActions',
          errorName: '<ButtonGroupActions />'
        },
        resultButtons: {
          // by default max is set to 1 (see childrenUtils.tsx)
          max: 5,

          isComponent: ResultButton,
          isSlot: 'resultButton',
          errorName: '<ResultButton />'
        },
        resultActionMenu: {
          isComponent: ResultActionMenu,
          isSlot: 'resultActionMenu',
          errorName: '<ResultActionMenu />'
        },
        resultOverflowMenu: {
          isComponent: ResultOverflowMenu,
          isSlot: 'resultOverflowMenu',
          errorName: '<ResultOverflowMenu />'
        }
      },
      { parentName: 'AutoResultItem' }
    )

    const resultButtonCount = (extracted.resultButtons as ReactElement<any>[])?.length ?? 0
    const resultActionMenuCount = extracted.resultActionMenu ? 1 : 0
    const resultOverflowMenuCount = extracted.resultOverflowMenu ? 1 : 0
    const hasResultButtons = resultButtonCount + resultActionMenuCount + resultOverflowMenuCount > 0
    const hasResultButtonGroup = !!extracted.buttonGroupActions
    const hasMetaText = !!extracted.meta
    const hasAdditionalInfo = !!extracted.additionalInfo
    const visibleFields = useVisibleFields()
    const attributes = isValidElement(extracted.resultAttributes)
      ? (extracted.resultAttributes as ReactElement<any>).props.children
      : []
    const fieldCount = attributes.length || (extracted.attributeColumns as ReactElement<any>[])?.length
    const attrColCount = extracted.visibleFieldAttributes ? visibleFields.length : fieldCount ?? 0
    const hasMiddleElements = attrColCount > 0
    const totalColCount = Number(!!extracted.title) + attrColCount

    const defaultTitleLockupColWidth = !hasMiddleElements ? '100%' : '312px'
    const defaultAttrColWidth = `${(100 / (totalColCount || 1)).toFixed(2)}%`
    const defaultMetaColWidth = metaColWidth ?? {
      '@base': hasMetaText || hasAdditionalInfo ? '25%' : `${resultButtonCount * 100}px`
    }

    // Get the badge's colorScheme so we can also use it for the gradientHeader
    const extractedBadgeVariant = extractVariantFromBadge(extracted.statusBadge)
    return (
      <ResultItem
        role="group"
        aria-label={childrenToString(extracted.title)}
        data-auto-result-item
        ref={ref}
        p={4}
        sx={merge(
          cq({
            containerType: 'inline-size',
            '[data-avatar-header]': {
              display: 'none'
            },
            '[data-result-title-lockup]': {
              mb: 'auto',
              mt: 0.25,
              flexBasis: { '@base': 'auto', '@5xl': defaultTitleLockupColWidth },
              minW: { '@base': 'auto', '@5xl': defaultTitleLockupColWidth },
              maxW: { '@base': 'full', '@5xl': defaultTitleLockupColWidth }
            },
            '[data-title-lockup-subscript][data-result-meta]': {
              position: 'relative',
              display: 'inline-block'
            },
            '[data-result-title-lockup]>[data-title-additional-info]': {
              display: 'flex',
              mt: { '@base': 0, '@3xl': 2 }
            },
            '[data-title-lockup-bottom]': {
              '[data-title-lockup-size="lg"] &': {
                fontSize: 'var(--vndly-font-size-xl)',
                lineHeight: { '@base': 6, '@5xl': 7 }
              },
              '[data-title-lockup-size="md"] &': {
                fontSize: 'var(--vndly-font-size-md)',
                lineHeight: { '@base': 5, '@5xl': 6 }
              }
            },
            '[data-result-middle]': {
              ...(isCenteredVertically && { alignSelf: { '@base': 'auto', '@5xl': 'center' } })
            },
            '[data-attribute-grid]': {
              justifyContent: { '@base': 'space-between', '@5xl': 'flex-start' },
              flexDirection: { '@base': 'column', '@xl': 'row' }
            },
            '[data-attribute-column]': {
              maxW: { '@base': 'full', '@5xl': defaultAttrColWidth },
              flexBasis: { '@base': 'full', '@5xl': defaultAttrColWidth },
              columnGap: { '@base': 0, '@xl': '24px' }
            },
            ...attributeColWidths.reduce(
              (obj: any, widthConfig: any, index: number) => ({
                ...obj,
                [`[data-attribute-column]:nth-child(${index + 1})`]: {
                  maxW: widthConfig,
                  flexBasis: widthConfig
                }
              }),
              {}
            ),
            '[data-result-attributes]': {
              gridTemplateColumns: { '@base': '1fr', '@2xl': 'repeat(3, minmax(0, 176px))' },
              '[data-show-more-results-button]': {
                display: { '@base': attrColCount > 3 ? 'block' : 'none', '@2xl': 'none' },
                justifySelf: 'flex-start'
              }
            },
            '[data-result-meta-column]': {
              display: { '@base': 'none', '@3xl': 'flex' },
              flexBasis: defaultMetaColWidth,
              maxW: defaultMetaColWidth,
              minW: defaultMetaColWidth
            },
            '[data-result-bottom]': {
              display: 'flex',
              gap: { '@base': '10px' },
              flexWrap: { '@base': 'wrap' },
              alignItems: { '@base': 'center', '@xl': 'flex-start' },
              justifyContent: { '@base': 'flex-start', '@xl': 'flex-end' }
            },
            '[data-result-button-group]': {
              display: 'flex',
              alignItems: 'center'
            },
            [LINKS_AND_BUTTONS_EXCLUDING_THE_TITLE]: {
              zIndex: 2
            }
          }),
          sx
        )}
        {...props}>
        {cloneSafely(extracted.gradientHeader, {}, { variant: extractedBadgeVariant })}
        {extracted.bulkCheckbox ?? extracted.checkbox}
        {cloneSafely(extracted.avatarGradientHeader, {}, { variant: extractedBadgeVariant })}
        <ResultContentWrap>
          <ResultMainContent>
            <ResultTitleLockup size={titleLockupSize} titleMaxWidth={defaultTitleLockupColWidth}>
              <TitleLockupMain>{cloneSafely(extracted.title, {}, { href })}</TitleLockupMain>
              <TitleLockupBottom>
                {extracted.favorite}
                {extracted.titleLockupIcon}
                {extracted.statusBadge}
                {extracted.statusSupport}
              </TitleLockupBottom>
              {hasAdditionalInfo && extracted.additionalInfo}
              {hasMetaText && (
                <TitleLockupSubscript data-hoist-above-parent-hover data-result-meta>
                  {extracted.meta}
                </TitleLockupSubscript>
              )}
              {extracted.subscript}
            </ResultTitleLockup>
            {hasMiddleElements && (
              <ResultMiddle>
                {extracted.resultAttributes ? (
                  <>{extracted.resultAttributes}</>
                ) : (
                  <AttributeGrid>{extracted.visibleFieldAttributes ?? extracted.attributeColumns}</AttributeGrid>
                )}
              </ResultMiddle>
            )}
          </ResultMainContent>
          <ResultBottom>
            {hasResultButtons && (
              <ResultButtonGroup>
                {extracted.resultButtons}
                {extracted.resultActionMenu}
                {extracted.resultOverflowMenu}
              </ResultButtonGroup>
            )}
            {hasResultButtonGroup && <>{extracted.buttonGroupActions}</>}
          </ResultBottom>
        </ResultContentWrap>
      </ResultItem>
    )
  }
)

AutoResultItem.displayName = 'AutoResultItem'

function extractVariantFromBadge(badgeElement: any): BadgeVariant {
  return (badgeElement as ReactElement<any>)?.props?.variant
}

/**
 * Result is a set of containers for laying out a list page item's contents, it works in tandem with:
 * - TitleLockup set of components
 * - AttributeGrid / Attribute set of components
 *
 * @example
 * <ResultItem>
 *   <ResultContentWrap>
 *     <ResultTitleLockup>
 *       ...TitleLockup Components...
 *     </ResultTitleLockup>
 *     <ResultMore>
 *       ...AttributeGrid Components...
 *     </ResultMore>
 *   </ResultContentWrap>
 * </ResultItem>
 */
/**
 * An <ItemsListItem> that allows passing of an optional href to make the whole row a link.
 */
export const ResultItem = forwardRef<BoxProps, 'div'>(({ sx, ...props }, ref) => {
  const actionBar = useActionBarContext() ?? {}
  // Grid mode styles each item as a card from ResultItems, so the inset row styles must not apply.
  const listStyles = actionBar.isGridDisplayMode
    ? {}
    : {
        '--vndly-items-list-start-radius': { $canvas: 'var(--chakra-radii-xl)' },
        '--vndly-items-list-end-radius': { $canvas: 'var(--chakra-radii-xl)' },
        borderRadius: { $canvas: 'xl' },
        borderTopWidth: { $canvas: 0 },
        px: { $canvas: 4 },
        // Uneven on purpose: text line-height already pads the bottom of each row, but nothing pads the top, so equal
        // padding put the content visibly closer to the divider above it than to the one below.
        pt: { $canvas: 6 },
        pb: { $canvas: 5 },
        '&:not(:first-of-type)::before': insetDivider(),
        [ROW_HOVERED]: {
          bg: { $base: 'blackAlpha.50', $canvas: 'var(--vndly-color-listitem-background-hover)' }
        },
        [`${ROW_HOVERED}::before`]: { opacity: 0 },
        [`${ROW_HOVERED} + &::before`]: { opacity: 0 },
        '[data-section-header] + &::before': { opacity: 0 },
        // ResultContentWrap's pl is the gap after the checkbox. Without one, text starts at the row's padding.
        '& > [data-result-content-wrap]': { pl: { $canvas: 0 } },
        '& > [data-checkbox] ~ [data-result-content-wrap]': { pl: { $canvas: 4 } }
      }
  return (
    <ItemsListItem
      data-result-item
      ref={ref}
      py={4}
      alignItems="flex-start"
      sx={$thm({ ...listStyles, ...sx })}
      {...props}
    />
  )
})

ResultItem.displayName = 'ResultItem'

export function useChangeBgColorOnHover(externalRef?: RefObject<HTMLDivElement | null>) {
  const innerRef = useRef(undefined)
  const ref = (externalRef ?? innerRef) as RefObject<HTMLDivElement | null>
  const onHoverChange = (isHovered = false) => {
    if (!ref.current) return
    ref.current.style.background = isHovered ? 'rgba(0,0,0,0.04)' : ''
  }
  const actionBar = useActionBarContext() ?? {}
  useHoverWithExcludedChildren({
    ref,
    onHoverChange,
    excludeQuerySelector: LINKS_AND_BUTTONS_EXCLUDING_THE_TITLE,
    isEnabled: !actionBar.isGridDisplayMode
  })
  return ref
}

type GradientHeaderProps = BoxProps & {
  variant?: BadgeVariant
}

export function ResultGradientHeader({ variant = 'incomplete', ...props }: GradientHeaderProps) {
  const isRebrand = useIsSanaRebrandEnabled()
  // Custom gradient styling for the animation on hover
  const variantToGradient: Record<BadgeVariant, string> = {
    incomplete: 'linear(45deg, gray.300 50%, gray.300 100%)',
    success:
      'linear(45deg, var(--vndly-color-feedback-background-success) 50%, var(--vndly-color-feedback-success) 100%)',
    danger: 'linear(45deg, red.100 50%, red.500 100%)',
    pending: 'linear(45deg, yellow.100 50%, yellow.500 100%)',
    complete: 'linear(45deg, gray.400 50%, gray.600 100%)',
    info: 'linear(45deg, blue.100 50%, blue.500 100%)'
  }

  return (
    <Box
      data-result-gradient-header
      sx={{
        position: 'absolute',
        display: 'none', // ResultItems will show this element in grid mode
        top: '-1px',
        left: '-1px',
        right: '-1px',
        // Rebrand: taller static strip (cards don't lift). Legacy: 8px that scaleY-grows on hover.
        height: isRebrand ? 3 : 2,
        ...(!isRebrand && { borderTopRadius: 'sm' }),
        bgGradient: variantToGradient[variant],
        transitionTimingFunction: 'ease-in-out',
        transitionDuration: '.35s',
        transitionProperty: isRebrand ? 'background-size' : 'transform, background-size',
        backgroundPosition: '100%',
        backgroundSize: '200%',
        ...(!isRebrand && {
          transformOrigin: 'bottom',
          transform: 'scaleY(1)'
        }),
        '[data-items-list-item]:hover &': isRebrand
          ? { backgroundSize: '100%' }
          : {
              transform: 'scaleY(2)',
              backgroundSize: '100%',
              borderTopRadius: '2px 1px'
            }
      }}
      {...props}
    />
  )
}

export function ResultContentWrap(props: StackProps) {
  return (
    <Flex
      data-result-content-wrap
      sx={cq({
        flexGrow: 1,
        justifyContent: 'space-between',
        minW: 0,
        h: 'full',
        gap: 4,
        flexDir: { '@base': 'column', '@xl': 'row' },
        alignItems: { '@base': 'stretch', '@xl': 'flex-start' },
        pl: 2
      })}
      {...props}
    />
  )
}

export function ResultMainContent(props: StackProps) {
  return (
    <Flex
      data-result-main-content
      justifyContent="space-between"
      gap={4}
      sx={cq({
        flexDir: { '@base': 'column', '@5xl': 'row' },
        alignItems: { '@base': 'stretch', '@5xl': 'flex-start' }
      })}
      {...props}
    />
  )
}
/**
 * Box for TitleLockup contents. Use instead of TitleLockup when putting TitleLockup inside a Result.
 * This way the TitleLockup and Attributes will properly wrap as the screen size changes.
 *
 * @example
 * <ResultTitleLockup> <-- instead of <TitleLockup>
 *   <TitleLockupMain>
 *     <TitleLockupHeading />
 *   </TitleLockupMain>
 *   <TitleLockupBottom />
 * </ResultTitleLockup>
 */
export function ResultTitleLockup({
  size = 'lg',
  titleMaxWidth = '25%',
  ...props
}: BoxProps & { size?: TitleLockupSize; titleMaxWidth?: number | string }) {
  return (
    <Box
      data-result-title-lockup
      data-title-lockup-size={size}
      sx={cq({
        flexDirection: 'column',
        alignItems: 'normal',
        flexBasis: titleMaxWidth,
        minW: { '@base': 'auto', '@lg': titleMaxWidth },
        maxW: { '@base': 'full', '@lg': titleMaxWidth }
      })}
      {...props}
    />
  )
}

/**
 * Box for all the other things that a Result might have: AttributeGrid, Call to Action Buttons, etc.
 */
export function ResultMiddle(props: BoxProps) {
  return (
    <Flex
      data-result-middle
      sx={cq({
        flex: 1,
        maxW: { '@base': 'full', '@5xl': 'none' },
        flexDirection: { '@base': 'column', '@5xl': 'row' }
      })}
      {...props}
    />
  )
}

/**
 * Area to place items below the main result item contents, typically for buttons on smaller screens.
 */
export function ResultBottom(props: BoxProps) {
  // Basis auto: start at the buttons' one-line width and only give space up (by wrapping labels) when the row is
  // short. A basis-0 bottom only gets leftover space, which crushes wrappable labels down to a word per line.
  return <Box data-result-bottom flex="1 1 auto" mb={2} {...props} />
}

export function ResultMetaColumn({ children, ...props }: BoxProps) {
  const extracted = useGroupChildren(
    children,
    {
      attributeText: {
        isComponent: AttributeText,
        isSlot: 'attributeText',
        errorName: '<AttributeText />'
      },
      resultButtonGroup: {
        isComponent: ResultButtonGroup,
        isSlot: 'resultButtonGroup',
        errorName: '<ResultButtonGroup />'
      }
    },
    { parentName: 'ResultMetaColumn' }
  )

  const attrTextCount = Number(!!extracted.attributeText)
  const buttonCount = Number(!!extracted.resultButtonGroup)
  const componentCount = attrTextCount + buttonCount

  return (
    <AttributeColumn
      data-result-meta-column
      flexGrow={0}
      flexShrink={1}
      direction="column"
      align="flex-end"
      textAlign="right"
      minW="auto"
      mt={componentCount === 2 ? '-4px !important' : 0}
      justifyContent={attrTextCount === 0 && buttonCount === 1 ? 'flex-start' : 'space-between'}
      sx={{
        alignItems: { '@base': 'flex-start', '@5xl': 'flex-end' },
        ml: { '@base': 4, '@5xl': 0 }
      }}
      {...props}>
      {children}
    </AttributeColumn>
  )
}

export function ResultMeta({ children }: { children: BoxProps['children'] }) {
  return <>{children}</>
}

type ResultButtonProps = ButtonProps & Partial<{ href: string }>

export const ResultButton = forwardRef<ResultButtonProps, 'button'>(({ children, href, ...props }, ref) => {
  return (
    <Button
      data-result-button
      href={href}
      ref={ref}
      variant="secondary"
      size="sm"
      flex={{ base: 1, sm: 'initial' }}
      maxW={{ sm: '240px' }}
      {...props}>
      {children}
    </Button>
  )
})

export const ResultActionMenu = forwardRef<MenuButtonProps, 'button'>(({ children, ...props }, ref) => (
  <Menu>
    <MenuButton data-result-action-menu ref={ref} size="sm" as={Button} rightIcon={<ChevronDown />} {...props}>
      <Msg id="common.ui.actions" />
    </MenuButton>
    <Portal>
      <MenuList>{children}</MenuList>
    </Portal>
  </Menu>
))

export const ResultOverflowMenu = forwardRef<MenuButtonProps, 'button'>(({ children, ...props }, ref) => (
  <Menu>
    <MenuButton
      data-result-overflow-menu
      ref={ref}
      size="sm"
      as={IconButton}
      icon={<MoreV width={4} height={4} />}
      {...props}
    />
    <Portal>
      <MenuList zIndex={2}>{children}</MenuList>
    </Portal>
  </Menu>
))

export function ResultButtonGroup({ children, ...props }: ButtonGroupProps) {
  return (
    <ButtonGroup data-result-button-group spacing={3} {...props}>
      {children}
    </ButtonGroup>
  )
}

/**
 * VisibleFieldAttributes
 *
 * Depending on whether visible fields are configured for the page (see ActionBar.VisibleFieldsButton), each
 * visible field is auto laid out using ResultAttributes component. On smaller container sizes, a "Show More"
 * button will be displayed.
 */
export function VisibleFieldAttributes({ attributeContents: AttributeContents }: { attributeContents: any }) {
  const fields = useVisibleFields()

  return (
    <ResultAttributes>
      {fields.map(field => (
        <AttributeContents key={field.key} field={field} />
      ))}
    </ResultAttributes>
  )
}

/**
 * ResultAttributes
 *
 * This component is a wrapper used to auto-layout list item data attributes into a maximum of
 * three columns for larger container sizes and a single column for small container sizes. When
 * there are more than 3 data attributes provided a "Show More" button will be displayed on small
 * container sizes.
 */
export function ResultAttributes({ children, ...props }: StackProps) {
  const attributes = children as ReactElement<any>[]
  const [showMore, setShowMore] = useState(false)

  return (
    <Grid data-result-attributes gap={4} {...props}>
      {/* Filter out any empty attributes */}
      {attributes?.filter(Boolean).map((item, index) => {
        if (index < 3) {
          return <GridItem key={item.key}>{item}</GridItem>
        }
        return (
          <GridItem
            key={item.key}
            sx={cq({
              display: { '@base': showMore ? 'block' : 'none', '@2xl': 'block' }
            })}>
            {item}
          </GridItem>
        )
      })}
      <Button
        data-show-more-results-button
        variant="link"
        textDecoration="underline"
        p={0}
        onClick={() => setShowMore(!showMore)}>
        {showMore ? <Msg id="common.ui.show_less" /> : <Msg id="common.ui.show_more" />}
      </Button>
    </Grid>
  )
}

export function AvatarGradientHeader({
  title,
  src,
  variant = 'incomplete',
  ...props
}: { title: string; src?: string } & GradientHeaderProps) {
  return (
    <Box data-avatar-header pr={6} {...props}>
      <GradientHeaderBackground variant={variant} />
      <Circle size={16} bg="gray.200" boxShadow="lg" border="4px solid white" position="relative" zIndex={1}>
        <Avatar name={title} bg="none" color="gray.500" size="lg" src={src} />
      </Circle>
    </Box>
  )
}

function GradientHeaderBackground({ variant = 'incomplete' }: { variant: BadgeVariant }) {
  const variantToGradient: Record<BadgeVariant, string> = {
    incomplete: 'incompleteGradient',
    success: 'successGradient',
    danger: 'dangerGradient',
    pending: 'warningGradient',
    complete: 'completeGradient',
    info: 'infoGradient'
  }

  return (
    <Box
      data-gradient-header
      display="none" // Display gradient header background only in grid mode
      position="absolute"
      top={0}
      left={0}
      overflow="hidden"
      zIndex={0}
      layerStyle={variantToGradient[variant]}
      w="full"
      h="56px">
      <BackgroundPattern />
    </Box>
  )
}

/**
 * A section header that can be placed between sections of items to visually group them.
 *
 * Just make sure that SectionHeader is a sibling of the AutoResultItems and
 * they are all direct children of the ResultsLayout.Items.
 *
 * <ResultsLayout.MainContent>
 *   <ResultsLayout.Items>
 *     {categories.map((category) => (
 *       <Fragment key={category.name}>
 *         <ResultsLayout.ItemsSectionHeader>
 *           {category.name}
 *         </ResultsLayout.ItemsSectionHeader>
 *         {category.items.map((item) => (
 *           <AutoResultItem key={item.id}>
 *             {...}
 *           </AutoResultItem>
 *         ))}
 *       </Fragment>
 *     ))}
 *   </ResultsLayout.Items>
 * </ResultsLayout.MainContent>
 */
export function ResultItemsSectionHeader(props: SectionHeaderProps) {
  const actionBar = useActionBarContext()
  if (actionBar.isGridDisplayMode) return null
  return (
    <SectionHeader
      _notFirst={{
        borderTop: '1px solid',
        borderTopColor: 'var(--vndly-color-container-border)'
      }}
      sx={$thm({
        // Same inset as the rows, so the label lines up with where row content starts.
        px: { $canvas: 4 },
        borderRadius: { $canvas: 'xl' },
        '&:not(:first-of-type)': { borderTop: { $canvas: 'none' } }
      })}
      {...props}
    />
  )
}
